import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { ChevronLeft, ChevronRight, CalendarCheck2, Loader2 } from "lucide-react";
import { ManageBooking } from "./ManageBooking";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-[#E2E8F0] bg-white text-[#0B1B2B] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors";

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const Scheduler = () => {
  const [month, setMonth] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [selected, setSelected] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", topic: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState(null);
  const [showManage, setShowManage] = useState(false);

  const today = new Date();
  const days = useMemo(() => {
    const first = new Date(month);
    const startPad = (first.getDay() + 6) % 7;
    const total = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    return [...Array(startPad).fill(null), ...Array.from({ length: total }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1))];
  }, [month]);

  useEffect(() => {
    if (!selected) return;
    setLoadingSlots(true);
    setSlot(null);
    api
      .get(`/schedule/availability`, { params: { date: selected } })
      .then(({ data }) => setSlots(data.slots))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [selected]);

  const book = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/schedule/book", { date: selected, slot, ...form });
      setConfirmed(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const isSelectable = (d) =>
    d && d.getDay() !== 0 && d.getDay() !== 6 && d > today;

  return (
    <section id="scheduler" className="py-20 md:py-32 bg-[#0B1B2B]" data-testid="scheduler-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs md:text-sm tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Executive Consultations</p>
        <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl text-white tracking-tight max-w-2xl">
          Request an Executive Briefing
        </h2>
        <p className="mt-5 text-slate-300 max-w-xl font-light">
          Select a 30-minute briefing window. Availability is computed in real time against our executive calendar; certain days are Executive Reserved.
        </p>
        <button onClick={() => setShowManage(!showManage)} className="mt-4 text-sm font-semibold text-[#D4AF37] gold-underline" data-testid="manage-booking-toggle">
          {showManage ? "Hide booking manager" : "Already booked? Manage your consultation"}
        </button>
        {showManage && <ManageBooking />}

        {confirmed ? (
          <div className="mt-12 max-w-lg bg-white rounded-xl shadow-2xl p-10 text-center" data-testid="booking-confirmation">
            <CalendarCheck2 size={36} className="text-[#D4AF37] mx-auto" />
            <h3 className="mt-5 font-serif text-2xl text-[#0B1B2B]">Consultation Confirmed</h3>
            <p className="mt-3 text-[#64748B]">
              {confirmed.date} at {confirmed.slot} ET
            </p>
            <div className="mt-5 px-6 py-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl inline-block">
              <div className="text-xs uppercase tracking-[0.15em] text-[#64748B]">Confirmation ID</div>
              <div className="font-serif text-lg font-bold text-[#0B1B2B]" data-testid="booking-confirmation-id">{confirmed.confirmation_id}</div>
            </div>
            <p className="mt-4 text-xs text-[#64748B]">A calendar confirmation has been dispatched to your email.</p>
            <button
              onClick={() => { setConfirmed(null); setSelected(null); setForm({ name: "", email: "", topic: "" }); }}
              className="mt-6 text-sm font-semibold text-[#0B1B2B] gold-underline"
              data-testid="book-another-button"
            >
              Book Another Window
            </button>
          </div>
        ) : (
          <div className="mt-12 lg:grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5 bg-white rounded-xl shadow-2xl p-7">
              <div className="flex items-center justify-between">
                <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="p-2 rounded-lg hover:bg-[#F8FAFC]" data-testid="calendar-prev-month" aria-label="Previous month">
                  <ChevronLeft size={18} />
                </button>
                <div className="font-serif text-lg font-semibold text-[#0B1B2B]" data-testid="calendar-month-label">
                  {month.toLocaleString("en-US", { month: "long", year: "numeric" })}
                </div>
                <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="p-2 rounded-lg hover:bg-[#F8FAFC]" data-testid="calendar-next-month" aria-label="Next month">
                  <ChevronRight size={18} />
                </button>
              </div>
              <div className="mt-5 grid grid-cols-7 gap-1 text-center text-xs font-semibold text-[#64748B] uppercase">
                {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
                  <div key={d} className="py-1">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 mt-1">
                {days.map((d, i) => (
                  <button
                    key={i}
                    disabled={!isSelectable(d)}
                    onClick={() => setSelected(iso(d))}
                    className={`aspect-square rounded-lg text-sm font-medium transition-all duration-200 ${
                      !d
                        ? "invisible"
                        : selected === iso(d)
                        ? "bg-[#0B1B2B] text-[#D4AF37] shadow-lg"
                        : isSelectable(d)
                        ? "text-[#0B1B2B] hover:bg-[#D4AF37]/15"
                        : "text-[#E2E8F0] cursor-not-allowed"
                    }`}
                    data-testid={d ? `calendar-day-${iso(d)}` : undefined}
                  >
                    {d ? d.getDate() : ""}
                  </button>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 mt-8 lg:mt-0 bg-white rounded-xl shadow-2xl p-7">
              {!selected ? (
                <div className="h-full flex items-center justify-center text-[#64748B] text-sm py-16" data-testid="scheduler-empty-state">
                  Select a weekday to view available windows.
                </div>
              ) : (
                <>
                  <h3 className="font-serif text-xl text-[#0B1B2B] font-semibold">Available Windows — {selected}</h3>
                  {loadingSlots ? (
                    <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#D4AF37]" /></div>
                  ) : slots.length === 0 ? (
                    <p className="mt-4 text-sm text-[#64748B]" data-testid="no-slots-message">No windows available on this date.</p>
                  ) : (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {slots.map((s) => (
                        <button
                          key={s.time}
                          disabled={!s.available}
                          onClick={() => setSlot(s.time)}
                          className={`px-5 py-2.5 rounded-xl text-sm font-semibold border transition-all duration-200 ${
                            slot === s.time
                              ? "bg-[#0B1B2B] text-[#D4AF37] border-[#0B1B2B]"
                              : s.available
                              ? "border-[#E2E8F0] text-[#0B1B2B] hover:border-[#D4AF37]"
                              : "border-[#E2E8F0] text-[#E2E8F0] line-through cursor-not-allowed"
                          }`}
                          data-testid={`slot-${s.time}`}
                        >
                          {s.time} ET
                        </button>
                      ))}
                    </div>
                  )}
                  {slot && (
                    <form onSubmit={book} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-[#E2E8F0] pt-6">
                      <input className={inputCls} placeholder="Full Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name-input" />
                      <input className={inputCls} type="email" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email-input" />
                      <input className={`${inputCls} md:col-span-2`} placeholder="Consultation Topic (optional)" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="booking-topic-input" />
                      {error && <p className="md:col-span-2 text-sm text-red-600" data-testid="booking-error">{error}</p>}
                      <button type="submit" disabled={busy} className="md:col-span-2 bg-[#D4AF37] text-[#0B1B2B] font-semibold py-3.5 rounded-xl hover:bg-[#e8c968] transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="booking-submit-button">
                        {busy && <Loader2 size={16} className="animate-spin" />}
                        Confirm {slot} ET on {selected}
                      </button>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
